(() => {
  'use strict';

  const RUNTIME_KEY = '__u20ProductFireRingV458396';
  const CARD_SELECTOR = '[data-u20-fire-ring-product]';
  const DESKTOP_QUERY = window.matchMedia('(min-width: 990px)');
  const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

  const DURATION = 2200;
  const FRAME_INTERVAL = 1000 / 30;
  const RESOLUTION_SCALE = 0.8;
  const PARTICLES_PER_FRAME = 4;
  const PARTICLE_SIZE = 6;
  const PARTICLE_VELOCITY = 1.6;
  const RANDOM_VELOCITY = 0.72;
  const PARTICLE_LIFE_MIN = 42;
  const PARTICLE_LIFE_MAX = 56;
  const START_INTERVAL = 500;
  const MAX_ACTIVE_EFFECTS = 3;
  const COLORS = ['#fff1b8', '#ffc45b', '#ff8a24', '#ff3d14'];

  if (window[RUNTIME_KEY]) {
    window[RUNTIME_KEY].refresh(document);
    return;
  }

  let observer = null;
  let cadenceTimer = 0;
  let lastLaunchTime = 0;
  let queue = [];
  const active = new Map();
  const preparedCards = new Set();

  const canRun = () => (
    DESKTOP_QUERY.matches &&
    !REDUCED_MOTION.matches &&
    !document.hidden
  );

  const randomBetween = (min, max) => Math.random() * (max - min) + min;

  const easeOutCubic = (value) => 1 - Math.pow(1 - value, 3);

  const removeFromQueue = (card) => {
    if (!card || card.dataset.u20FireRingQueued !== 'true') return;
    card.dataset.u20FireRingQueued = 'false';
    queue = queue.filter((queuedCard) => queuedCard !== card);
  };

  const clearCadence = () => {
    if (cadenceTimer) window.clearTimeout(cadenceTimer);
    cadenceTimer = 0;
    queue.forEach((card) => { card.dataset.u20FireRingQueued = 'false'; });
    queue = [];
    lastLaunchTime = 0;
  };

  const stop = (card) => {
    const state = active.get(card);
    if (!state) return;
    if (state.frame) window.cancelAnimationFrame(state.frame);
    state.resizeObserver?.disconnect();
    state.canvas.remove();
    active.delete(card);
    drainQueue();
  };

  const play = (card) => {
    if (!card || active.has(card) || !canRun() || card.dataset.u20FireRingInView !== 'true') return false;
    const imageBox = card.querySelector('.product-item__image');
    if (!imageBox) return false;

    const canvas = document.createElement('canvas');
    canvas.className = 'u20-local-cap-fire-ring';
    canvas.setAttribute('aria-hidden', 'true');
    imageBox.prepend(canvas);

    const context = canvas.getContext('2d', { alpha: true });
    if (!context) {
      canvas.remove();
      return false;
    }

    const state = {
      canvas,
      context,
      particles: [],
      frame: 0,
      startTime: 0,
      lastFrameTime: 0,
      radiusX: 1,
      radiusY: 1,
      resizeObserver: null
    };

    const resize = () => {
      const width = Math.max(1, Math.round(imageBox.clientWidth * RESOLUTION_SCALE));
      const height = Math.max(1, Math.round(imageBox.clientHeight * RESOLUTION_SCALE));
      if (canvas.width === width && canvas.height === height) return;
      canvas.width = width;
      canvas.height = height;
      state.radiusX = width * 0.36;
      state.radiusY = height * 0.32;
    };

    const spawnParticle = (ringScale) => {
      const angle = Math.random() * Math.PI * 2;
      const cosine = Math.cos(angle);
      const sine = Math.sin(angle);
      const radiusX = state.radiusX * ringScale;
      const radiusY = state.radiusY * ringScale;
      const tangentX = -radiusX * sine;
      const tangentY = radiusY * cosine;
      const tangentLength = Math.hypot(tangentX, tangentY) || 1;
      const life = Math.round(randomBetween(PARTICLE_LIFE_MIN, PARTICLE_LIFE_MAX));

      state.particles.push({
        x: cosine * radiusX + randomBetween(-2, 2),
        y: sine * radiusY + randomBetween(-2, 2),
        vx: (tangentX / tangentLength) * PARTICLE_VELOCITY + randomBetween(-RANDOM_VELOCITY, RANDOM_VELOCITY),
        vy: (tangentY / tangentLength) * PARTICLE_VELOCITY + randomBetween(-RANDOM_VELOCITY, RANDOM_VELOCITY),
        life,
        startLife: life,
        size: randomBetween(PARTICLE_SIZE * 0.58, PARTICLE_SIZE),
        color: COLORS[Math.floor(Math.random() * COLORS.length)]
      });
    };

    const draw = (time) => {
      if (!active.has(card) || !canRun() || card.dataset.u20FireRingInView !== 'true') {
        stop(card);
        return;
      }

      if (!state.startTime) state.startTime = time;
      const progress = Math.min(1, (time - state.startTime) / DURATION);
      if (progress >= 1) {
        stop(card);
        return;
      }

      state.frame = window.requestAnimationFrame(draw);
      if (time - state.lastFrameTime < FRAME_INTERVAL) return;
      state.lastFrameTime = time - ((time - state.lastFrameTime) % FRAME_INTERVAL);

      let ringScale = 1;
      if (progress < 0.2) {
        ringScale = 0.28 + (0.72 * easeOutCubic(progress / 0.2));
      } else if (progress < 0.72) {
        ringScale = 1 + (Math.sin((progress - 0.2) * Math.PI * 5) * 0.018);
      } else {
        ringScale = 1 + (0.12 * ((progress - 0.72) / 0.28));
      }

      const spawnCount = progress < 0.72 ? PARTICLES_PER_FRAME : 2;
      for (let index = 0; index < spawnCount; index += 1) {
        spawnParticle(ringScale);
      }

      context.setTransform(1, 0, 0, 1, 0, 0);
      context.clearRect(0, 0, canvas.width, canvas.height);
      context.setTransform(1, 0, 0, 1, canvas.width / 2, canvas.height / 2);
      context.globalCompositeOperation = 'lighter';

      const radiusX = state.radiusX * ringScale;
      const radiusY = state.radiusY * ringScale;
      const endingOpacity = progress > 0.72 ? 1 - ((progress - 0.72) / 0.28) : 1;

      for (let index = state.particles.length - 1; index >= 0; index -= 1) {
        const particle = state.particles[index];
        particle.x += particle.vx;
        particle.y += particle.vy;

        const normalizedX = particle.x / Math.max(radiusX, 1);
        const normalizedY = particle.y / Math.max(radiusY, 1);
        const distance = Math.hypot(normalizedX, normalizedY) || 1;
        const correction = (1 - distance) * 0.12;
        particle.vx += (normalizedX / distance) * correction;
        particle.vy += (normalizedY / distance) * correction;
        particle.vx *= 0.988;
        particle.vy *= 0.988;
        particle.life -= 1;

        if (particle.life <= 0) {
          state.particles.splice(index, 1);
          continue;
        }

        const lifeRatio = particle.life / particle.startLife;
        const ignition = Math.min(1, (1 - lifeRatio) * 5);
        context.globalAlpha = Math.pow(lifeRatio, 1.25) * ignition * endingOpacity;
        context.fillStyle = particle.color;
        context.beginPath();
        context.arc(
          particle.x,
          particle.y,
          particle.size * (0.3 + ((1 - lifeRatio) * 0.9)),
          0,
          Math.PI * 2
        );
        context.fill();
      }

      context.globalAlpha = 1;
      context.globalCompositeOperation = 'source-over';
    };

    resize();
    if ('ResizeObserver' in window) {
      state.resizeObserver = new ResizeObserver(resize);
      state.resizeObserver.observe(imageBox);
    }
    active.set(card, state);
    state.frame = window.requestAnimationFrame(draw);
    return true;
  };

  function drainQueue() {
    if (cadenceTimer || !canRun() || active.size >= MAX_ACTIVE_EFFECTS) return;

    queue = queue.filter((card) => (
      card.isConnected !== false &&
      card.dataset.u20FireRingQueued === 'true' &&
      card.dataset.u20FireRingInView === 'true' &&
      !active.has(card)
    ));
    if (!queue.length) return;

    const elapsed = performance.now() - lastLaunchTime;
    const delay = lastLaunchTime ? Math.max(0, START_INTERVAL - elapsed) : 0;
    cadenceTimer = window.setTimeout(() => {
      cadenceTimer = 0;
      const card = queue.shift();
      if (!card) return;
      card.dataset.u20FireRingQueued = 'false';
      if (play(card)) lastLaunchTime = performance.now();
      drainQueue();
    }, delay);
  }

  const enqueue = (card) => {
    if (
      !card ||
      active.has(card) ||
      card.dataset.u20FireRingQueued === 'true' ||
      card.dataset.u20FireRingInView !== 'true'
    ) return;
    card.dataset.u20FireRingQueued = 'true';
    queue.push(card);
    drainQueue();
  };

  const getObserver = () => {
    if (observer || !canRun() || !('IntersectionObserver' in window)) return observer;
    observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const card = entry.target;
        if (entry.isIntersecting && entry.intersectionRatio >= 0.35) {
          if (card.dataset.u20FireRingInView !== 'true') {
            card.dataset.u20FireRingInView = 'true';
            enqueue(card);
          }
        } else if (!entry.isIntersecting || entry.intersectionRatio <= 0.02) {
          card.dataset.u20FireRingInView = 'false';
          removeFromQueue(card);
          stop(card);
        }
      });
    }, { threshold: [0.02, 0.35] });
    return observer;
  };

  const prepare = (card) => {
    if (!card || preparedCards.has(card)) return;
    preparedCards.add(card);
    card.dataset.u20FireRingInView = 'false';
    card.dataset.u20FireRingQueued = 'false';
    getObserver()?.observe(card);
  };

  const refresh = (scope = document) => {
    const cards = scope.matches?.(CARD_SELECTOR)
      ? [scope, ...scope.querySelectorAll(CARD_SELECTOR)]
      : [...scope.querySelectorAll(CARD_SELECTOR)];
    cards.forEach(prepare);
  };

  const rebuildObserver = () => {
    observer?.disconnect();
    observer = null;
    clearCadence();
    preparedCards.forEach((card) => {
      card.dataset.u20FireRingInView = 'false';
      stop(card);
    });
    if (!canRun()) return;
    const nextObserver = getObserver();
    preparedCards.forEach((card) => nextObserver?.observe(card));
  };

  const addMediaListener = (query) => {
    const listener = rebuildObserver;
    if (typeof query.addEventListener === 'function') query.addEventListener('change', listener);
    else query.addListener(listener);
  };

  document.addEventListener('visibilitychange', rebuildObserver);
  document.addEventListener('shopify:section:load', (event) => refresh(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll(CARD_SELECTOR).forEach((card) => {
      removeFromQueue(card);
      stop(card);
      observer?.unobserve(card);
      preparedCards.delete(card);
    });
  });
  addMediaListener(DESKTOP_QUERY);
  addMediaListener(REDUCED_MOTION);

  refresh(document);
  window[RUNTIME_KEY] = { refresh };
})();
